import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { captureEvent } from "@/lib/analyticsQueue";
import { useCredits } from "@/contexts/CreditsContext";
import { useCreditCosts } from "@/hooks/useCreditCosts";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGeneratedImages } from "@/hooks/useGeneratedImages";
import { useImageDelete } from "@/hooks/useImageDelete";
import { editImage } from "@/lib/api/editImage";
import { improvePrompt } from "@/lib/api/improvePrompt";
import { useImageUpload } from "@/hooks/useImageUpload";
import { ACCEPTED_IMAGE_TYPES } from "@/config/media";
import { MAX_REFERENCE_IMAGES } from "@/config/constants";
import { downloadMedia, detectAspectRatio, shareMedia } from "@/lib/mediaUtils";
import { ASPECT_RATIOS, RESOLUTIONS } from "@/components/flowly/AspectRatioPicker/constants";
import { useModels } from "@/hooks/useModels";
import { useModelWarning } from "@/hooks/useModelWarning";
import { useGenerationRestore } from "@/hooks/useGenerationRestore";
import { captureGenerationError } from "@/lib/sentry";
import {
  clearFlowState,
  useFlowPersistence,
} from "@/hooks/useFlowPersistence";
import { FLOW_KEYS } from "@/config/flowKeys";
import { imageToDisplayMedia } from "@/lib/types/media";
import type { DisplayMedia } from "@/lib/types/media";
import { useLightboxNavigation } from "@/hooks/useLightboxNavigation";
import { usePersistentPendingBoost } from "@/hooks/usePersistentPendingBoost";
import { ANALYTICS_EVENTS, FLOW_IDS } from "@shared/schemas/analytics";

const FLOW_STATE_KEY = FLOW_KEYS.IMAGE_GENERATOR;

// --- Hook ---

export function useImageGenerator() {
  const location = useLocation();
  const navigate = useNavigate();
  const { deductCredits, refreshCredits, handleCreditError } = useCredits();
  const { getImageEditCost, getImageModelCost, costsLoading } = useCreditCosts();
  const { profile } = useAuth();
  const { t, language } = useLanguage();
  const { normalizeOrWarn, normalizeFilesOrWarn } = useImageUpload();
  const { models, refetchModels } = useModels("image");

  // --- Refs ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);

  // --- State ---

  const initialImageUrl = location.state?.imageUrl || "";
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [prompt, setPrompt] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [resolution, setResolution] = useState<string>("2k");
  const [imageModel, setImageModel] = useState<string>("");
  const modelWarning = useModelWarning(imageModel, models);
  const [detectedAspectRatio, setDetectedAspectRatio] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isEnhancingPrompt, setIsEnhancingPrompt] = useState(false);
  const [selectedImage, setSelectedImage] = useState<DisplayMedia | null>(null);
  const [showMaskCanvas, setShowMaskCanvas] = useState(false);
  const [maskImageUrl, setMaskImageUrl] = useState<string | null>(null);
  const [maskBlob, setMaskBlob] = useState<Blob | null>(null);
  const [referenceImages, setReferenceImages] = useState<Array<{ id: string; url: string }>>([]);

  // --- Gallery ---
  const { images: dbImages, loading: imagesLoading, refetch: refetchImages, pendingImages, pendingCount: dbPendingCount, completingIds: imageCompletingIds, failedImages } = useGeneratedImages(undefined, "edit");
  const { deleteImageByUrl, deleteFailedImages } = useImageDelete();

  // Persistent pending boost — survives page refresh via sessionStorage
  const { pendingCount: pendingEdits, increment: incrementPending, decrement: decrementPending } =
    usePersistentPendingBoost({ pipeline: "edit", dbPendingCount, isDbLoading: imagesLoading });

  // Restore prompt & image from pending DB items on page refresh
  useGenerationRestore({
    pendingItems: pendingImages,
    isLoading: imagesLoading,
    currentPrompt: prompt,
    setPrompt,
    currentImage: imageUrl || null,
    setImage: (val) => setImageUrl(val ?? ""),
  });

  // Derive gallery from completed dbImages (auto-updates via Realtime)
  const galleryImages: DisplayMedia[] = useMemo(
    () => dbImages.filter((img) => img.image_url).map(imageToDisplayMedia),
    [dbImages],
  );

  // Navigation for gallery lightbox
  const { currentMediaIndex, canNavigate, goToNextItem, goToPrevItem } =
    useLightboxNavigation(galleryImages, selectedImage, setSelectedImage);

  // Handle same-route navigation (e.g. "Edit Image" or "Regenerate" from lightbox while already on /generate-image)
  useEffect(() => {
    if (location.state?.imageUrl || location.state?.prompt) {
      if (location.state?.imageUrl) setImageUrl(location.state.imageUrl);
      if (location.state?.prompt) setPrompt(location.state.prompt);
      window.history.replaceState({}, document.title);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.state?.imageUrl, location.state?.prompt]);

  // Set default model from registry when models load (prefers isDefault flag)
  useEffect(() => {
    if (!imageModel && models.length > 0) {
      const defaultModel = models.find((m) => m.isDefault) ?? models[0];
      setImageModel(defaultModel.id);
    }
  }, [models, imageModel]);

  // --- Analytics: tool page view ---
  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.TOOL_PAGE_VIEW, { flow: FLOW_IDS.IMAGE_GENERATOR });
  }, []);

  // --- Handlers ---

  const handleFileSelect = async (rawFile: File) => {
    const file = await normalizeOrWarn(rawFile);
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return;
    captureEvent(ANALYTICS_EVENTS.IMAGE_UPLOAD, { flow: FLOW_IDS.IMAGE_GENERATOR });
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImageUrl(result);

      // Detect aspect ratio from image
      const img = new Image();
      img.onload = () => {
        const detected = detectAspectRatio(img.width, img.height);
        setDetectedAspectRatio(detected);
        const supported = selectedModel?.capabilities.supportedAspectRatios;
        if (supported && !supported.includes(detected)) {
          setAspectRatio(supported[0] ?? "1:1");
        } else {
          setAspectRatio(detected);
        }
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleClearImage = () => {
    setImageUrl("");
    setMaskImageUrl(null);
    setMaskBlob(null);
    setReferenceImages([]);
  };

  const handleMaskConfirm = (greenMaskUrl: string, _combinedDataUrl: string, bwMaskBlob: Blob) => {
    setMaskImageUrl(greenMaskUrl);
    setMaskBlob(bwMaskBlob);
    setShowMaskCanvas(false);
  };

  const selectedModel = models.find((m) => m.id === imageModel);

  const maxReferenceImages = Math.min(
    selectedModel?.capabilities.maxReferenceImages ?? MAX_REFERENCE_IMAGES,
    MAX_REFERENCE_IMAGES,
  );

  const availableAspectRatios = useMemo(() => {
    const supported = selectedModel?.capabilities.supportedAspectRatios;
    if (!supported) return ASPECT_RATIOS;
    return ASPECT_RATIOS.filter((r) => supported.includes(r.id));
  }, [selectedModel]);

  const availableResolutions = useMemo(() => {
    const supported = selectedModel?.capabilities.supportedResolutions;
    if (!supported) return RESOLUTIONS;
    return RESOLUTIONS.filter((r) => supported.includes(r.id));
  }, [selectedModel]);

  const handleReferenceImagesAdd = async (rawFiles: FileList | File[]) => {
    const remaining = maxReferenceImages - referenceImages.length;
    const rawList = Array.from(rawFiles).slice(0, remaining);

    const filesToProcess = await normalizeFilesOrWarn(rawList);
    if (!filesToProcess) return;

    filesToProcess.forEach((file) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = e.target?.result as string;
        setReferenceImages((prev) => {
          if (prev.length >= maxReferenceImages) return prev;
          return [...prev, { id: `ref-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, url }];
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleReferenceImageRemove = (id: string) => {
    setReferenceImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleImageModelChange = (modelId: string) => {
    setImageModel(modelId);
    const newModel = models.find((m) => m.id === modelId);
    const supported = newModel?.capabilities.supportedAspectRatios;
    if (supported && !supported.includes(aspectRatio)) {
      setAspectRatio(supported[0] ?? "1:1");
    }
    const supportedRes = newModel?.capabilities.supportedResolutions;
    if (supportedRes && !supportedRes.includes(resolution)) {
      setResolution(supportedRes[0]);
    }
    // Trim reference images if new model has a lower limit
    const newMax = Math.min(
      newModel?.capabilities.maxReferenceImages ?? MAX_REFERENCE_IMAGES,
      MAX_REFERENCE_IMAGES,
    );
    setReferenceImages((prev) => (prev.length > newMax ? prev.slice(0, newMax) : prev));
  };

  const handleResolutionChange = (newResolution: string) => {
    setResolution(newResolution);
  };

  const generateImage = async () => {
    if (!prompt.trim()) {
      toast.error(t.imageGenerator.enterEditInstructions, {
        description: t.imageGenerator.enterEditInstructionsDesc,
      });
      return;
    }

    const plan = profile?.subscription_name;
    if (isGenerating && plan !== "pro" && plan !== "creator") {
      toast.info(t.generate.generationInProgress, { description: t.generate.parallelProRequired });
      return;
    }

    const totalCost = quantity * getImageEditCost(resolution, imageModel);

    if (!deductCredits(totalCost)) {
      return;
    }

    setIsGenerating(true);
    incrementPending(quantity);

    setTimeout(() => {
      galleryRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);

    let succeeded = false;
    try {
      const response = await editImage({
        sourceImage: imageUrl || undefined,
        prompt: prompt,
        aspectRatio: aspectRatio,
        resolution: resolution,
        modelId: imageModel,
        quantity: quantity,
        maskBlob: maskBlob || undefined,
        referenceImages: referenceImages.length > 0 ? referenceImages.map((img) => img.url) : undefined,
      });

      if (response.success && (response.image || response.images?.length)) {
        toast.success(t.imageGenerator.editCompleted, {
          description: t.imageGenerator.editCompletedDesc,
        });
        succeeded = true;
      } else if (!handleCreditError(response)) {
        toast.error(t.imageGenerator.editFailed, {
          description: response.error || t.generate.generationFailedWithRefund,
        });
      }
    } catch (error) {
      console.error("Error generating image:", error);
      captureGenerationError(error, {
        flow: FLOW_IDS.IMAGE_GENERATOR,
        action: "generate",
        model: imageModel,
        endpoint: "/api/edit-image",
      });
      refetchModels();
      if (!handleCreditError(error)) {
        const errorMessage = error instanceof Error ? error.message : undefined;
        toast.error(t.imageGenerator.editFailed, {
          description: errorMessage || t.generate.generationFailedWithRefund,
        });
      }
    } finally {
      setIsGenerating(false);
      decrementPending(quantity);
      await refreshCredits();
      if (succeeded) {
        // Wipe the persisted draft AND reset the per-image inputs to defaults
        // — you don't re-edit from the same source/prompt. Results show in
        // the DB-backed gallery, untouched here. quantity/resolution/model
        // are sticky pickers, left as-is.
        clearFlowState(FLOW_STATE_KEY);
        if (maskImageUrl?.startsWith("blob:")) URL.revokeObjectURL(maskImageUrl);
        setImageUrl("");
        setPrompt("");
        setMaskImageUrl(null);
        setMaskBlob(null);
        setReferenceImages([]);
        setDetectedAspectRatio(null);
      }
    }
  };

  // Auth gate lives inside GenerateButton, so this is just a rename.
  const handleGenerate = generateImage;

  // Persist prompt + settings AND the image inputs across the auth/credit
  // wall. `imageUrl` / `referenceImages` / `maskBlob` are image keys → their
  // bytes go to IndexedDB (no localStorage quota cliff), restored back to the
  // exact representation generate() sends (data URLs / Blob).
  useFlowPersistence({
    key: FLOW_STATE_KEY,
    state: { prompt, quantity, aspectRatio, resolution, imageModel, imageUrl, referenceImages, maskBlob },
    imageKeys: ["imageUrl", "referenceImages", "maskBlob"],
    onRestore: (s) => {
      if (typeof s.prompt === "string") setPrompt(s.prompt);
      if (typeof s.quantity === "number") setQuantity(s.quantity);
      if (typeof s.aspectRatio === "string") setAspectRatio(s.aspectRatio);
      if (typeof s.resolution === "string") setResolution(s.resolution);
      if (typeof s.imageModel === "string") setImageModel(s.imageModel);
      if (typeof s.imageUrl === "string" && s.imageUrl) setImageUrl(s.imageUrl);
      if (Array.isArray(s.referenceImages)) {
        setReferenceImages(s.referenceImages as Array<{ id: string; url: string }>);
      }
      if (s.maskBlob instanceof Blob) {
        setMaskBlob(s.maskBlob);
        setMaskImageUrl(URL.createObjectURL(s.maskBlob));
      }
    },
  });

  const handleEnhancePrompt = async () => {
    if (!prompt.trim() || isEnhancingPrompt) return;
    captureEvent(ANALYTICS_EVENTS.PROMPT_ENHANCE_CLICK, { flow: FLOW_IDS.IMAGE_GENERATOR });
    setIsEnhancingPrompt(true);
    try {
      const mode = maskBlob ? "inpaint" : "general";
      const response = await improvePrompt({ prompt, mode });
      if (response.success && response.improvedPrompt) {
        setPrompt(response.improvedPrompt);
        toast.success(t.imageGenerator.promptImproved, {
          description: t.imageGenerator.promptImprovedDesc,
        });
      } else if (!response.isRateLimited) {
        toast.error(t.imageGenerator.enhanceFailed, {
          description: response.error || t.imageGenerator.tryAgain,
        });
      }
    } catch (error) {
      console.error("Error improving prompt:", error);
      captureGenerationError(error, {
        flow: FLOW_IDS.IMAGE_GENERATOR,
        action: "enhance_prompt",
        endpoint: "/api/improve-prompt",
      });
      toast.error(t.common.error, {
        description: t.imageGenerator.enhanceError,
      });
    } finally {
      setIsEnhancingPrompt(false);
    }
  };

  const handleClearFailed = () => {
    if (failedImages.length > 0) {
      deleteFailedImages(failedImages.map((f) => f.id));
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // --- Lightbox callbacks ---

  const handleCloseLightbox = () => setSelectedImage(null);

  const handleRegenerate = () => {
    if (selectedImage) {
      setImageUrl(selectedImage.referenceImageUrls[0] || "");
      setPrompt(selectedImage.prompt || "");
      setSelectedImage(null);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
    }
  };

  const handleAnimate = () => {
    if (selectedImage) {
      navigate("/frames-to-video", { state: { animateImage: selectedImage.url } });
    }
  };

  const handleLightboxDownload = () => {
    if (selectedImage) downloadMedia(selectedImage.url, "todoai-generated", "png");
  };

  const handleShare = () => {
    if (!selectedImage?.id) return;
    void shareMedia({ id: selectedImage.id, type: "image" });
  };

  const handleDelete = async () => {
    if (!selectedImage) return;
    const urlToDelete = selectedImage.url;
    setSelectedImage(null);
    await deleteImageByUrl(urlToDelete);
  };

  const handleGalleryItemClick = (item: DisplayMedia) => {
    setSelectedImage(item);
  };

  const handleDisabledClick = useCallback(() => {
    document.querySelector('[data-scroll-target="prompt"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // --- Return ---

  return {
    // Refs
    fileInputRef,
    galleryRef,

    // Upload
    imageUrl,
    isDragging,
    handleFileSelect,
    handleDrop,
    handleClearImage,
    onDragOver,
    onDragLeave,

    // Prompt
    prompt,
    setPrompt,
    isEnhancingPrompt,
    handleEnhancePrompt,

    // Generation options
    quantity,
    setQuantity,
    aspectRatio,
    setAspectRatio,
    availableAspectRatios,
    resolution,
    availableResolutions,
    handleResolutionChange,
    imageModel,
    handleImageModelChange,
    getEditCreditCost: getImageEditCost,
    getImageModelCost,
    costsLoading,

    // Mask
    maskImageUrl,
    showMaskCanvas,
    setShowMaskCanvas,
    handleMaskConfirm,

    // Reference images
    referenceImages,
    maxReferenceImages,
    handleReferenceImagesAdd,
    handleReferenceImageRemove,

    // Generation
    isGenerating,
    handleGenerate,
    pendingEdits,
    pendingImages,
    imageCompletingIds,
    failedImages,
    handleClearFailed,

    // Models
    models,
    modelWarning,

    // Gallery
    galleryImages,
    handleGalleryItemClick,

    // Lightbox
    selectedImage,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    handleCloseLightbox,
    handleRegenerate,
    handleAnimate,
    handleLightboxDownload,
    handleShare,
    handleDelete,

    // Disabled click
    handleDisabledClick,

    // i18n
    t,
    language,
  };
}
